import { Alert } from "../../../../ui/alert";

import { EDSButton } from "../../../../eds/EDSButton";
import { useDiaryContext } from "@/hooks/useDiaryContext";
import { useShowAICoPilot } from "@/hooks/useShowAICoPilot";
import { ProcessedScanResult, ScanResults } from "../SpaceScanner.types";
import { OptimisationReport } from "../AIInsights/OptimisationReport";
import { HistoricalComparison } from "../AIInsights/HistoricalComparison";
import { calculateOptimisationScore } from "@/services/ai/SpaceOptimiserAI";
import { getResultCounts } from "../SpaceScannerStage2/SpaceScannerStage2Controller";

/**
 * Space Scanner Result Props
 */
type SpaceScannerResultProps = {
  result: ScanResults;
  onClose: () => void;
};

/**
 * Space Scanner Result
 * @param param0
 * @returns
 */
export const SpaceScannerResult = ({
  result,
  onClose
}: SpaceScannerResultProps) => {
  const showAICoPilot = useShowAICoPilot();
  const { venueConfig } = useDiaryContext();
  const results: ProcessedScanResult[] = Array.isArray(result)
    ? result
    : result.results;
  const failures = Array.isArray(result) ? [] : result.failures;
  const totalBookingsToMove = results.filter(
    (r: ProcessedScanResult) => r.moved
  ).length;
  const totalBookings = results.length;
  const successfulMoves = results.filter(
    (r: ProcessedScanResult) => r.moved && r.completed && !r.failed
  ).length;
  const failedMoves = failures.length;

  // Get detailed breakdown counts
  const {
    lockedCount,
    startedCount,
    guestlistCount,
    optimalCount,
    leftCount,
    waitlistedCount,
    noshowCount,
    areaClosedCount,
    unassignedCount
  } = getResultCounts(results);

  // Calculate total excluded (not moved due to exclusion reasons)
  const totalExcluded =
    lockedCount +
    startedCount +
    guestlistCount +
    leftCount +
    waitlistedCount +
    noshowCount +
    areaClosedCount;

  // Check if we should show the detailed breakdown
  const showDetailedBreakdown =
    totalExcluded > 0 || unassignedCount > 0 || optimalCount > 0;

  return (
    <div className="flex flex-1 flex-col min-h-0">
      <div className="flex-shrink-0 flex items-center py-3 sm:py-[var(--panelPadding)]">
        {failedMoves === 0 && (
          <p className="text-sm leading-relaxed sm:text-base">
            Your diary has been updated.
          </p>
        )}
        {failedMoves > 0 && failedMoves < totalBookingsToMove && (
          <p className="text-sm leading-relaxed sm:text-base">
            Your diary has been partially updated.
          </p>
        )}
        {failedMoves > 0 && failedMoves === totalBookingsToMove && (
          <p className="text-sm leading-relaxed sm:text-base">
            Failed to update your diary.
          </p>
        )}
      </div>
      <div className="overflow-y-auto flex-1 min-h-0">
        <div>
          {successfulMoves > 0 && (
            <Alert variant="success" className="my-4">
              {successfulMoves} booking
              {successfulMoves !== 1 ? "s were" : " was"} moved.{" "}
              {totalBookings - totalBookingsToMove - unassignedCount} stayed in
              their current area.
            </Alert>
          )}
          {failedMoves > 0 && (
            <Alert variant="destructive" className="my-4">
              {failedMoves} booking{failedMoves !== 1 ? "s" : ""} failed to
              save. Please try again or contact support if the issue persists.
            </Alert>
          )}
          {totalBookingsToMove === 0 && (
            <Alert variant="info" className="my-4">
              No bookings needed to be moved.
            </Alert>
          )}
          {showAICoPilot && venueConfig?._id && (
            <HistoricalComparison
              currentScore={calculateOptimisationScore(results).score}
              venueId={venueConfig._id}
            />
          )}
          {showDetailedBreakdown && (
            <section
              className="my-4 rounded-lg border border-[var(--borderColor,#e5e7eb)] p-3"
              aria-label="Optimisation breakdown"
            >
              <h4 className="mb-2 text-sm font-semibold text-[var(--primaryTextColor)]">
                Breakdown
              </h4>
              <ul className="space-y-1 text-xs text-[var(--secondaryTextColor)]">
                {successfulMoves > 0 && (
                  <li className="flex items-center gap-2">
                    <span className="size-2 rounded-full bg-[var(--successColor,#22c55e)]" />
                    {successfulMoves} booking
                    {successfulMoves !== 1 ? "s" : ""} moved successfully
                  </li>
                )}
                {optimalCount > 0 && (
                  <li className="flex items-center gap-2">
                    <span className="size-2 rounded-full bg-[var(--infoColor,#3b82f6)]" />
                    {optimalCount} booking
                    {optimalCount !== 1 ? "s" : ""} already optimally assigned
                  </li>
                )}
                {lockedCount > 0 && (
                  <li className="flex items-center gap-2">
                    <span className="size-2 rounded-full bg-[var(--warningColor,#f59e0b)]" />
                    {lockedCount} booking{lockedCount !== 1 ? "s" : ""} locked
                  </li>
                )}
                {startedCount > 0 && (
                  <li className="flex items-center gap-2">
                    <span className="size-2 rounded-full bg-[var(--warningColor,#f59e0b)]" />
                    {startedCount} booking{startedCount !== 1 ? "s" : ""}{" "}
                    already started
                  </li>
                )}
                {guestlistCount > 0 && (
                  <li className="flex items-center gap-2">
                    <span className="size-2 rounded-full bg-[var(--warningColor,#f59e0b)]" />
                    {guestlistCount} guestlist booking
                    {guestlistCount !== 1 ? "s" : ""}
                  </li>
                )}
                {leftCount > 0 && (
                  <li className="flex items-center gap-2">
                    <span className="size-2 rounded-full bg-[var(--warningColor,#f59e0b)]" />
                    {leftCount} booking{leftCount !== 1 ? "s" : ""} left
                  </li>
                )}
                {waitlistedCount > 0 && (
                  <li className="flex items-center gap-2">
                    <span className="size-2 rounded-full bg-[var(--warningColor,#f59e0b)]" />
                    {waitlistedCount} booking{waitlistedCount !== 1 ? "s" : ""}{" "}
                    on waitlist
                  </li>
                )}
                {noshowCount > 0 && (
                  <li className="flex items-center gap-2">
                    <span className="size-2 rounded-full bg-[var(--warningColor,#f59e0b)]" />
                    {noshowCount} booking{noshowCount !== 1 ? "s" : ""} marked
                    as no-show
                  </li>
                )}
                {areaClosedCount > 0 && (
                  <li className="flex items-center gap-2">
                    <span className="size-2 rounded-full bg-[var(--warningColor,#f59e0b)]" />
                    {areaClosedCount} booking{areaClosedCount !== 1 ? "s" : ""}{" "}
                    with closed target areas
                  </li>
                )}
                {unassignedCount > 0 && (
                  <li className="flex items-center gap-2">
                    <span className="size-2 rounded-full bg-[var(--destructiveColor,#ef4444)]" />
                    {unassignedCount} booking{unassignedCount !== 1 ? "s" : ""}{" "}
                    could not be assigned
                  </li>
                )}
              </ul>
            </section>
          )}
          {showAICoPilot && <OptimisationReport venueId={venueConfig?._id} />}
        </div>
      </div>
      <div className="flex-shrink-0 flex flex-wrap items-end justify-between gap-2 pt-3 sm:gap-4 sm:pt-[var(--panelPadding)]">
        <EDSButton className="flex-1" onClick={onClose}>
          Close and view diary
        </EDSButton>
      </div>
    </div>
  );
};
